import { Component } from '../lib/ComponentBase';
import { registerComponent } from '../lib/ComponentHelpers';
import type { DisplayProvider, DisplayInfo } from '../types/Components';

export const SPEEDS = {
  '1/6x': 10,
  '1/5x': 12,
  '1/4x': 15,
  '1/3x': 20,
  '1/2x': 30,
  standard: 60,
  '2x': 120,
  '3x': 180,
  '4x': 240,
  '5x': 300,
  '6x': 360,
};

export type SpeedName = keyof typeof SPEEDS;

const speedNameValues = Object.keys(SPEEDS);

/**
 * Component that holds stats for an actor, such as speed and health.
 */
class ActorStats extends Component implements DisplayProvider {
  speedName: SpeedName;
  health: number;

  constructor(data: { speedName?: SpeedName; health?: number } = {}) {
    super();
    const speedName = data.speedName ?? 'standard';
    if (!speedNameValues.includes(speedName)) {
      throw new Error(`[ActorStats] Invalid speed name: "${speedName}"`);
    }

    this.speedName = speedName;
    this.health = data.health ?? 100;
  }

  getDisplayInfo(): DisplayInfo {
    const speedLabels: Record<string, string> = {
      'standard': '1x', '2x': '2x', '3x': '3x', '4x': '4x', '5x': '5x', '6x': '6x',
      '1/2x': '\u00BDx', '1/3x': '\u2153x', '1/4x': '\u00BCx', '1/5x': '\u2155x', '1/6x': '\u2159x',
    };
    const glyph = speedLabels[this.speedName] ?? this.speedName;
    return {
      icon: glyph,
      label: `Speed: ${this.speedName}`,
      description: this.speedName === 'standard' ? 'Moves at normal speed.' : `Moves at ${this.speedName} speed.`,
    };
  }

  get speed(): number {
    return SPEEDS[this.speedName];
  }

  equals(other: this): boolean {
    return this.speedName === other.speedName &&
           this.health === other.health;
  }

  toJSON() {
    return {
      speedName: this.speedName,
      health: this.health,
    };
  }

  static getTestInstances(): ActorStats[] {
    return [
      new ActorStats({ speedName: 'standard', health: 100 }),
      new ActorStats({ speedName: '2x', health: 50 }),
      new ActorStats({ speedName: '1/2x', health: 150 }),
    ];
  }
}

export default registerComponent(ActorStats);
